import type {
  CreateOrderDTO,
  OrderListQueryDTO,
  ShippingBranchOptionsQueryDTO,
  UpdateOrderDetailsDTO,
  UpdateOrderDTO,
} from "@/modules/orders/domain/contracts";
import type {
  CustomersSearchResult,
  OrderDetailResult,
  OrdersListResult,
  ShippingBranchOptionsResult,
} from "@/modules/orders/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type OrderListQuery = OrderListQueryDTO;

export const ordersApi = {
  async list(query?: OrderListQuery): Promise<OrdersListResult> {
    const url = new URL(`${config.apiUrl}/orders`);
    if (query?.status) url.searchParams.set("status", query.status);
    if (query?.search) url.searchParams.set("search", query.search);
    if (query?.dateFrom) url.searchParams.set("dateFrom", query.dateFrom);
    if (query?.dateTo) url.searchParams.set("dateTo", query.dateTo);
    if (query?.province) url.searchParams.set("province", query.province);
    if (query?.district) url.searchParams.set("district", query.district);
    url.searchParams.set("limit", String(query?.limit ?? 20));
    url.searchParams.set("offset", String(query?.offset ?? 0));
    return fetcher.get(url.toString());
  },
  async get(id: string): Promise<OrderDetailResult> {
    return fetcher.get(`${config.apiUrl}/orders/${id}`);
  },
  async create(input: CreateOrderDTO): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders`, input);
  },
  async update(id: string, input: UpdateOrderDTO): Promise<OrderDetailResult> {
    return fetcher.patch(`${config.apiUrl}/orders/${id}`, input);
  },
  async markPaid(id: string): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/mark-paid`, {});
  },
  async markReady(id: string): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/mark-ready`, {});
  },
  async markShipped(
    id: string,
    input?: { trackingNumber?: string | null },
  ): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/mark-shipped`, input ?? {});
  },
  async markDelivered(id: string): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/mark-delivered`, {});
  },
  async markPickedUp(id: string): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/mark-picked-up`, {});
  },
  async markReturned(
    id: string,
    input?: {
      returnNote?: string | null;
      returnShippingCost?: number | null;
      outboundCarrierCost?: number | null;
    },
  ): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/mark-returned`, input ?? {});
  },
  async reopenForShipment(id: string): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/reopen-for-shipment`, {});
  },
  async revertStatus(id: string): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/revert-status`, {});
  },
  async updateShipping(
    id: string,
    input: {
      shippingCarrier?: string | null;
      shippingBranch?: string | null;
      trackingNumber?: string | null;
    },
  ): Promise<OrderDetailResult> {
    return fetcher.patch(`${config.apiUrl}/orders/${id}/shipping`, input);
  },
  async updateDetails(
    id: string,
    input: UpdateOrderDetailsDTO,
  ): Promise<OrderDetailResult> {
    return fetcher.patch(`${config.apiUrl}/orders/${id}/details`, input);
  },
  async cancel(id: string): Promise<OrderDetailResult> {
    return fetcher.post(`${config.apiUrl}/orders/${id}/cancel`, {});
  },
  async searchCustomers(q: string): Promise<CustomersSearchResult> {
    const { customersApi } = await import(
      "@/modules/customers/presentation/api/client"
    );
    return customersApi.search(q);
  },
  async shippingBranchOptions(
    query: ShippingBranchOptionsQueryDTO,
  ): Promise<ShippingBranchOptionsResult> {
    const url = new URL(`${config.apiUrl}/orders/shipping-branch-options`);
    url.searchParams.set("carrier", query.carrier);
    if (query.province) url.searchParams.set("province", query.province);
    if (query.district) url.searchParams.set("district", query.district);
    if (query.village) url.searchParams.set("village", query.village);
    if (query.q) url.searchParams.set("q", query.q);
    return fetcher.get(url.toString());
  },
};
